import React, { useState } from 'react';
import { getApi } from '../utils/api';
import TaskStatusTracker from './TaskStatusTracker';
import ImportJobStatus from './ImportJobStatus';

interface CSVImportProps {
  orgId: string;
  onImportComplete?: (jobData: any) => void;
}

const CSVImport: React.FC<CSVImportProps> = ({ orgId, onImportComplete }) => {
  const [file, setFile] = useState<File | null>(null);
  const [validationTaskId, setValidationTaskId] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<any | null>(null);
  const [importJobId, setImportJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const api = getApi();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
      // Reset states when file changes
      setValidationTaskId(null);
      setValidationResult(null);
      setImportJobId(null);
      setError(null);
    }
  };

  const handleValidate = async () => {
    if (!file) return;

    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('org_id', orgId);

      // Debug log
      console.log("FormData details:");
      console.log("- File:", file.name, file.type, file.size);
      console.log("- Organization ID:", orgId);
      
      // Validate org_id is a valid UUID
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(orgId)) {
        console.error("Organization ID is not a valid UUID:", orgId);
        throw new Error("Invalid organization ID format. Expected UUID.");
      }

      // Check form data content
      // Avoid using for...of loop with formData.entries() due to TS downlevelIteration issues
      console.log("FormData entries:");
      formData.forEach((value, key) => {
        console.log(`- ${key}:`, value);
      });
      
      // Don't set Content-Type header manually for FormData
      // Let the browser set it automatically with the correct boundary
      const response = await api.post('/v1/import/validate', formData);
      console.log("Validation response:", response.data);
      setValidationTaskId(response.data.task_id);
    } catch (err: any) {
      console.error('Error uploading file:', err);
      console.error('Error details:', err.response?.data);
      setError(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handleValidationComplete = (result: any) => {
    console.log("Validation complete:", result);
    setValidationResult(result);
  };

  const handleProcessImport = async () => {
    if (!validationResult || validationResult.status !== 'success') {
      setError('File validation failed. Please validate the file first.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const response = await api.post('/v1/import/process', {
        file_path: validationResult.file_path,
        org_id: orgId,
      });
      
      console.log("Process import response:", response.data);
      setImportJobId(response.data.import_job_id);
    } catch (err: any) {
      console.error('Error processing import:', err);
      setError(err.response?.data?.detail || err.message || 'Import processing failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImportComplete = (jobData: any) => {
    console.log("Import complete:", jobData);
    if (onImportComplete) {
      onImportComplete(jobData);
    }
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-6">
      <h2 className="text-xl font-semibold mb-4">Import Complaints from CSV</h2>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded mb-4">
          <p>{error}</p>
        </div>
      )}

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Select CSV File</label>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded file:border-0
                    file:text-sm file:font-medium
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100"
          disabled={isUploading || !!importJobId}
        />
        <p className="mt-1 text-xs text-gray-500">
          Need help formatting your CSV?{' '}
          <a
            href="/dashboard/import-guide"
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:text-blue-800 font-semibold"
          >
            Read the CSV Bulk Import Guide
          </a>
          .
        </p>
        <div className="mt-2 text-xs text-gray-500 bg-gray-50 p-2 border border-gray-200 rounded space-y-2">
          <div className="flex justify-between items-center">
            <p className="font-semibold">Quick reference</p>
            <a
              href="/sample_complaints_template.csv"
              download
              className="text-blue-600 hover:text-blue-800 text-xs flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download Template
            </a>
          </div>
          <ul className="list-disc list-inside space-y-1">
            <li><span className="font-medium">Core columns:</span> customer name, verbatim text, channel, received date/time.</li>
            <li><span className="font-medium">Optional metadata:</span> customer contact info, IDs, product/policy, journey stage, links.</li>
            <li><span className="font-medium">Threads:</span> use <code>thread_messages</code> JSON or transcript columns (Customer:/Agent:) for multi-message conversations.</li>
          </ul>
        </div>
      </div>

      {file && !validationTaskId && (
        <button 
          onClick={handleValidate} 
          disabled={isUploading || !file}
          className="w-full py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {isUploading ? 'Uploading...' : 'Validate CSV'}
        </button>
      )}

      {validationTaskId && !validationResult && (
        <div className="mt-4">
          <h3 className="text-sm font-medium mb-2">Validation Status</h3>
          <TaskStatusTracker
            taskId={validationTaskId}
            onComplete={handleValidationComplete}
          />
        </div>
      )}

      {validationResult && validationResult.status === 'success' && !importJobId && (
        <>
          <div className="p-3 bg-green-50 rounded border border-green-200 mb-4">
            <h3 className="text-sm font-medium text-green-800">Validation Successful</h3>
            <p className="text-sm text-green-700 mt-1">
              File is valid and contains {validationResult.row_count} complaints ready for import.
            </p>
            <div className="mt-2 text-xs text-gray-600">
              <strong>Columns detected:</strong> {validationResult.columns?.join(', ')}
            </div>
          </div>

          <button 
            onClick={handleProcessImport} 
            disabled={isProcessing}
            className="w-full py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {isProcessing ? 'Processing...' : 'Start Import'}
          </button>
        </>
      )}

      {validationResult && validationResult.status === 'failed' && (
        <div className="p-3 bg-red-50 rounded border border-red-200 mt-4">
          <h3 className="text-sm font-medium text-red-800">Validation Failed</h3>
          <p className="text-sm text-red-700 mt-1">
            {validationResult.error}
          </p>
          <button 
            className="mt-2 px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50"
            onClick={() => {
              setValidationTaskId(null);
              setValidationResult(null);
            }}
          >
            Upload Another File
          </button>
        </div>
      )}

      {importJobId && (
        <div className="mt-4">
          <h3 className="text-sm font-medium mb-2">Import Progress</h3>
          <ImportJobStatus
            importJobId={importJobId}
            onComplete={handleImportComplete}
          />
        </div>
      )}
    </div>
  );
};

export default CSVImport;